import { DateFormats, DateOrTime, formatPrice } from '@nextgen-web-framework/core';
import { Entity, OrderHistoryResponse, OrderLine } from '../services';
import { CTAs, IOrderHistory, IOrderHistoryTransform, IProductWithImageInterface, SOURCE_OMS } from '../types';
import { getProductInfo, isBundleProduct, isOrderCancelled } from '../utils';
import { checkChildOrder, checkParentOrder } from '../utils/orderDetailsUtil';
import {
  channelSpecificCheckOHCTA,
  dateTimeFormatter,
  getBusinessNature,
  getCalculatedOrderStatus,
  getFulfillmentType,
  getOrderCancellationDetails,
  isABO,
  isMember,
} from '../utils/commonUtils';

function getOrderAmount(
  amount: number | undefined,
  groupAmount: number | undefined,
  isoCurrencyCode: string | undefined,
  groupOrderId: string | undefined,
  calculatedGroupOrderDetailView: boolean | undefined,
) {
  const amountStr = calculatedGroupOrderDetailView && groupOrderId ? groupAmount : amount;
  return formatPrice(amountStr || 0, isoCurrencyCode);
}

function getOrderData(
  order: Entity,
  productInfo: Array<IProductWithImageInterface>,
  loggedInAccountId: string,
  hasCancelledItems: boolean,
  hasTNAItems: boolean,
) {
  return {
    productData: productInfo,
    orderId: order?.salesOrderId || '',
    isABOOrder: isABO(getBusinessNature(loggedInAccountId, order || {})),
    isMemberOrder: isMember(getBusinessNature(loggedInAccountId, order || {})),
    calculatedOrderStatus: getCalculatedOrderStatus(order),
    orderDateTime: dateTimeFormatter(order.orderDateTime, DateFormats.MEDIUM_DATE, undefined, DateOrTime.DATE_ONLY),
    paidStatus:
      order.sourceOms === SOURCE_OMS.NEXTGEN ? order?.calculatedPaymentStatus || '' : order?.paymentStatus || '',
    totalAmount: getOrderAmount(
      order?.totalOrderValue,
      order?.groupOrderTotalValue,
      order?.isoCurrencyCode,
      order?.groupOrderId,
      order.calculatedGroupOrderDetailView,
    ),
    totalPVAmount: order.totalPVAmount?.toString() || '',
    totalBVAmount: order.totalBVAmount?.toString() || '',
    totalAmPoints: order.totalMemberPoints || 0,
    currencyCode: order?.isoCurrencyCode,
    cartNumber: order?.calculatedGroupCount,
    isGroupParentOrder: order?.groupOrderId && order.calculatedGroupOrderDetailView ? true : false,
    orderedBy: order?.orderingSalesAccount?.name || '',
    orderedFor: order?.orderForSalesAccount?.name || '',
    orderedByAccountId: order?.orderingSalesAccount?.accountId || '',
    orderedForAccountId: order?.orderForSalesAccount?.accountId || '',
    isParentOrder: checkParentOrder(loggedInAccountId, order),
    isChildOrder: checkChildOrder(loggedInAccountId, order),
    isBackOrder: order?.calculatedHasBackOrderLines,
    hasCancelledItems: hasCancelledItems,
    hasTNAItems: hasTNAItems,
    inventoryReservationId: order?.inventoryReservationId,
    isCancelledOrder: isOrderCancelled(getCalculatedOrderStatus(order)),
    cancellationDetails: getOrderCancellationDetails(order?.orderLines)?.[0],
    showBuyAgainCTA: channelSpecificCheckOHCTA(CTAs.BUY_AGAIN, order?.calculatedShowBuyAgainCta, order?.sourceOms),
    showRetryCTA: channelSpecificCheckOHCTA(CTAs.RETRY_PAYMENT, order?.calculatedShowRetryCta, order?.sourceOms),
    enableRetryCTA: order?.calculatedEnableRetryCta,
    showPayNowCTA: channelSpecificCheckOHCTA(CTAs.PAY_NOW, order?.calculatedShowPayNowCta, order?.sourceOms),
    enablePayNowCTA: order?.calculatedEnablePayNowCta,
    showReturnCTA: channelSpecificCheckOHCTA(CTAs.REQUEST_RETURN, order?.calculatedShowReturnCta, order?.sourceOms),
    enableReturnCTA: order?.calculatedEnableReturnCta,
    isReplacementOrder: order?.isReplacementOrder,
    eventDateTime: order?.originSourceTimestamp,
    fulfillmentType: getFulfillmentType(order),
    sourceOms: order?.sourceOms,
    isPaymentAwaitedAndAllVoided: order?.isPaymentAwaitedAndAllVoided,
  };
}

function prepareProductData(order: Entity) {
  const productInfo: Array<IProductWithImageInterface> = [];
  let hasCancelledItems = false;
  let hasTNAItems = false;
  order?.orderLines?.forEach((product: OrderLine, index) => {
    const isBundle = isBundleProduct(product);
    productInfo[index] = getProductInfo(
      product,
      order?.isoCurrencyCode || '',
      false,
      isBundle,
      false,
      order?.isReplacementOrder,
    );

    if (
      Number(productInfo[index]?.cancelledQuantity) > 0 ||
      (isBundle &&
        product?.bundleProducts?.find((bundle) => bundle?.cancelledQuantity && bundle?.cancelledQuantity > 0))
    ) {
      hasCancelledItems = true;
    }
    if (Number(product?.unallocatedQuantity) > 0 && Number(product?.cancelledQuantity) === 0) {
      hasTNAItems = true;
    }
  });

  return {
    productInfo: productInfo,
    hasCancelledItems: hasCancelledItems,
    hasTNAItems: hasTNAItems,
  };
}

export function transformOrderHistoryData(response: OrderHistoryResponse, loggedInAccountId: string): IOrderHistory {
  const data: Array<IOrderHistoryTransform> = [];

  if (response.orders && response.orders.length) {
    response.orders.forEach((order, index) => {
      const productsData = prepareProductData(order);
      data[index] = getOrderData(
        order,
        productsData?.productInfo,
        loggedInAccountId,
        productsData?.hasCancelledItems,
        productsData?.hasTNAItems,
      );
    });
  }
  const transformedData: IOrderHistory = {
    orders: data,
    total: response.pagination?.totalPages || 0,
    totalRecords: response.pagination?.totalCounts || 0,
  };
  return transformedData;
}
